import { ref, watch, toValue, onBeforeUnmount } from "vue";
import { tokenStorage } from "@/shared/auth/tokenStorage";

const BLOB_CACHE_LIMIT = 100;
const blobCache = new Map();

function cacheGet(key) {
  const blob = blobCache.get(key);
  if (blob) {
    blobCache.delete(key);
    blobCache.set(key, blob);
  }
  return blob;
}

function cacheSet(key, blob) {
  blobCache.set(key, blob);
  if (blobCache.size > BLOB_CACHE_LIMIT) {
    blobCache.delete(blobCache.keys().next().value);
  }
}

function toBackendUrl(rawUrl) {
  const base = window.__CONFIG__?.VUE_APP_BASE_DOMAIN || "";
  let path = rawUrl;
  if (/^https?:\/\//i.test(rawUrl)) {
    try {
      const u = new URL(rawUrl);
      path = u.pathname + u.search;
    } catch {
      return "";
    }
  }
  if (!path.startsWith("/")) path = `/${path}`;
  return base + path;
}

export function useAuthedImage(source) {
  const src = ref("");
  const error = ref(false);
  const loading = ref(false);
  let objectUrl = "";
  let requestId = 0;
  let controller = null;

  function revoke() {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = "";
    }
    src.value = "";
  }

  function showBlob(blob) {
    revoke();
    objectUrl = URL.createObjectURL(blob);
    src.value = objectUrl;
  }

  async function load(rawUrl) {
    if (controller) controller.abort();
    revoke();
    error.value = false;
    loading.value = false;
    if (!rawUrl) return;

    const url = toBackendUrl(rawUrl);
    if (!url) {
      error.value = true;
      return;
    }

    const cached = cacheGet(url);
    if (cached) {
      showBlob(cached);
      return;
    }

    const id = ++requestId;
    controller = new AbortController();
    loading.value = true;
    try {
      const token = tokenStorage.accessToken;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
        signal: controller.signal,
      });
      if (id !== requestId) return;
      if (!res.ok) {
        error.value = true;
        return;
      }
      const blob = await res.blob();
      if (id !== requestId) return;
      cacheSet(url, blob);
      showBlob(blob);
    } catch (e) {
      if (e?.name === "AbortError" || id !== requestId) return;
      error.value = true;
    } finally {
      if (id === requestId) loading.value = false;
    }
  }

  watch(
    () => toValue(source),
    (url) => load(url),
    { immediate: true }
  );

  onBeforeUnmount(() => {
    if (controller) controller.abort();
    revoke();
  });

  return { src, error, loading };
}
